import { useCallback, useMemo, useState } from 'react';
import { QueryGroup, QueryRule } from '../../types/QueryBuilder';
import { createEmptyGroup, generateId, getFieldsByCategory, isGroupValid } from '../../utils/queryBuilderUtils';
import { useQueryBuilderData } from '../../context/QueryBuilderDataContext';

const MAX_GROUPS = 2;

const updateGroupRules = (groups: QueryGroup[], groupId: string, update: (rules: QueryRule[]) => QueryRule[]) =>
    groups.map(group => (group.id === groupId ? { ...group, rules: update(group.rules) } : group));

export const useQueryBuilder = () => {
    const { fields, loadingFields, errorFields, categories } = useQueryBuilderData();
    const [groups, setGroups] = useState<QueryGroup[]>(() => [createEmptyGroup()]);

    const addRule = useCallback((groupId: string) => {
        const category = categories[0] || '';
        const rule: QueryRule = {
            id: generateId(),
            category,
            field: getFieldsByCategory(fields, category)[0]?.name || '',
            operator: '=',
            value: '',
        };
        setGroups(prev => updateGroupRules(prev, groupId, rules => [...rules, rule]));
    }, [categories, fields]);

    const removeRule = useCallback((groupId: string, ruleId: string) => {
        setGroups(prev => updateGroupRules(prev, groupId, rules => rules.filter(rule => rule.id !== ruleId)));
    }, []);

    const updateRule = useCallback((groupId: string, ruleId: string, data: Partial<QueryRule>) => {
        setGroups(prev => updateGroupRules(prev, groupId, rules =>
            rules.map(rule => (rule.id === ruleId ? { ...rule, ...data } : rule))
        ));
    }, []);

    const addGroup = useCallback(() => {
        setGroups(prev => (prev.length < MAX_GROUPS ? [...prev, createEmptyGroup()] : prev));
    }, []);

    const removeGroup = useCallback((groupId: string) => {
        setGroups(prev => prev.filter(group => group.id !== groupId));
    }, []);

    const resetGroups = useCallback(() => setGroups([createEmptyGroup()]), []);

    const isValid = useMemo(() => groups.length > 0 && groups.every(isGroupValid), [groups]);

    return {
        groups,
        categories,
        isValid,
        addRule,
        removeRule,
        updateRule,
        addGroup,
        removeGroup,
        resetGroups,
        setGroups,
        fields,
        loadingFields,
        fieldsError: errorFields,
    };
};
